import { ILegalEntityResponseSchema } from '@monetize/types/app';
import {
  DEFAULT_PAGER,
  FilterType,
  TDataTablePager,
} from '@monetize/types/common';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { legalEntitiesServiceQueryKeys } from '../../../api/legalEntityService';
import {
  useGetListData,
  usePerformEntityAction,
} from '../../../api/queryUtils';
import { updateListCacheWithUpdatedItem } from '../../../api/queryUtilsHelpers';
import {
  MButton,
  MFlex,
  MPageContainer,
  MSettingsPageHeader,
} from '../../../components/Monetize';
import { DataTableActions } from '../../../components/Monetize/DataTable/MDataTableActions';
import { SortingState } from '../../../components/Monetize/DataTableNew/appTable';
import { useTableColumns } from '../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { ACTION_COLUMN_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { ROUTES } from '../../../constants';
import { buildFilterParamsRequestObject } from '../../../utils';
import { navigateOrOpenNewWindow } from '../../../utils/misc';
import {
  LEGAL_ENTITY_LIST_CATALOG,
  LegalEntityListColumnContext,
} from './legalEntityList.columns';

const defaultPager: TDataTablePager = {
  ...DEFAULT_PAGER,
  sortField: 'name',
  sortOrder: 1,
};

const INITIAL_SORTING_STATE: SortingState = [{ id: 'name', desc: false }];

const EMPTY_PROPS = {
  mainMessage: 'Looks like there are no legal entities here.',
  btnLabel: 'New Entity',
  to: ROUTES.LEGAL_ENTITY_NEW,
};

export const LegalEntitySettings = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pager, setPager] = useState<TDataTablePager>(defaultPager);
  const [filters, setFilters] = useState<FilterType[]>([]);
  const filterParams = useMemo(
    () => buildFilterParamsRequestObject(filters),
    [filters],
  );

  const { data: legalEntities, isLoading } =
    useGetListData<ILegalEntityResponseSchema>('legalEntities', {
      config: pager,
      filters: filterParams,
    });

  const { mutate: setToDefaultLegalEntity } =
    usePerformEntityAction<ILegalEntityResponseSchema>('legalEntities', {
      onSuccess: (legalEntityData) => {
        updateListCacheWithUpdatedItem(
          queryClient,
          [...legalEntitiesServiceQueryKeys.legalEntityList()],
          { ...legalEntityData, defaultEntity: false },
        );
        queryClient.invalidateQueries({
          queryKey: [...legalEntitiesServiceQueryKeys.legalEntityList()],
        });
      },
      meta: { showErrorToast: true },
    });

  const actionsTemplate = useCallback(
    ({ id, defaultEntity }: ILegalEntityResponseSchema) => {
      if (defaultEntity) {
        return null;
      }
      const actions = [
        {
          title: 'Set to Default Entity',
          enabled: true,
          action: () => setToDefaultLegalEntity({ id, action: 'default' }),
        },
      ];
      return (
        <MFlex alignItems="center">
          <DataTableActions actions={actions} />
        </MFlex>
      );
    },
    [setToDefaultLegalEntity],
  );

  const columnContext = useMemo<LegalEntityListColumnContext>(
    () => ({ actionBodyTemplate: actionsTemplate }),
    [actionsTemplate],
  );
  const { columns, state: columnState } = useTableColumns(
    LEGAL_ENTITY_LIST_CATALOG,
    columnContext,
  );

  return (
    <MPageContainer alignItems="stretch" data-testid="legal-entity-list-page">
      <MSettingsPageHeader title="Legal Entities">
        <MButton
          variant="primary"
          onClick={() => navigate(ROUTES.LEGAL_ENTITY_NEW)}
        >
          New
        </MButton>
      </MSettingsPageHeader>

      <DataTable
        columns={columns}
        state={columnState}
        data={legalEntities?.content || []}
        isLoading={isLoading}
        enablePagination
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={legalEntities?.totalElements || 0}
        totalPages={legalEntities?.totalPages}
        initialSortingState={INITIAL_SORTING_STATE}
        pager={pager}
        setPager={setPager}
        initialFilters={filters}
        onFiltersChange={(newFilters) => {
          setFilters(newFilters);
        }}
        onRowClick={(row, _tableRow, openInNewWindow) =>
          navigateOrOpenNewWindow(
            navigate,
            ROUTES.getLegalEntityEditRoute(row.id),
            openInNewWindow,
          )
        }
        emptyProps={EMPTY_PROPS}
      />
    </MPageContainer>
  );
};
